'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import BottomNav from '@/components/BottomNav';
import PageHeader from '@/components/PageHeader';
import EventBanner from '@/components/EventBanner';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const CATEGORIES = ['General', 'Activities', 'Food', 'Stalls', 'Accessibility', 'Parking', 'Other'];

interface FeedbackForm {
  name: string;
  email: string;
  rating: number;
  category: string;
  message: string;
}

export default function FeedbackPage() {
  const [form, setForm] = useState<FeedbackForm>({
    name: '',
    email: '',
    rating: 0,
    category: 'General',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleRating = (val: number) => {
    setForm((prev) => ({ ...prev, rating: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.message.trim()) {
      setError('Please enter your feedback message.');
      return;
    }
    if (form.rating === 0) {
      setError('Please select a rating.');
      return;
    }
    setSubmitting(true);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('feedback_submissions').insert({
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        rating: form.rating,
        category: form.category,
        message: form.message.trim(),
      });
      if (dbError) throw dbError;
      setSubmitted(true);
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <EventBanner />
      <main className="pb-nav">
        <div className="gradient-hero px-4 pt-6 pb-8">
          <div className="max-w-lg mx-auto">
            <h1 className="text-2xl font-bold text-white mb-1">Feedback</h1>
            <p className="text-white/60 text-sm">Tell us what you think about the event</p>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-4 pb-8">
          {submitted ? (
            <div className="bg-card rounded-2xl p-8 border border-border card-shadow text-center">
              <CheckCircleIcon className="w-16 h-16 text-accent mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Thank You!</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Your feedback has been submitted. We really appreciate you taking the time to share your thoughts — it helps us make the event even better next year!
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setForm({ name: '', email: '', rating: 0, category: 'General', message: '' });
                }}
                className="mt-6 gradient-primary text-secondary font-bold text-sm px-6 py-3 rounded-xl touch-target active:scale-95 transition-all duration-150"
              >
                Submit More Feedback
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Rating */}
              <div className="bg-card rounded-2xl p-4 border border-border card-shadow">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                  Overall Rating <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRating(star)}
                      className="transition-transform duration-150 active:scale-90"
                      suppressHydrationWarning
                    >
                      {star <= form.rating ? (
                        <StarIcon className="w-10 h-10 text-primary" />
                      ) : (
                        <StarOutline className="w-10 h-10 text-muted-foreground/40" />
                      )}
                    </button>
                  ))}
                </div>
                {form.rating > 0 && (
                  <p className="text-center text-xs text-muted-foreground mt-2 font-medium">
                    {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][form.rating]}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="bg-card rounded-2xl p-4 border border-border card-shadow">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Category
                </label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, category: cat }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                        form.category === cat
                          ? 'gradient-primary text-secondary' :'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      suppressHydrationWarning
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="bg-card rounded-2xl p-4 border border-border card-shadow">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Your Feedback <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us about your experience..."
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 resize-none"
                />
              </div>

              {/* Optional name/email */}
              <div className="bg-card rounded-2xl p-4 border border-border card-shadow space-y-3">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Your Details (Optional)
                </p>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name..."
                    className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150"
                  />
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-sm font-medium text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 gradient-primary text-secondary font-bold text-sm py-3.5 rounded-xl touch-target transition-all duration-150 active:scale-95 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-secondary/30 border-t-secondary rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </form>
          )}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
